import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  FormControlLabel,
  Switch,
  InputAdornment
} from '@mui/material';
import { FaInstagram, FaFacebook, FaWhatsapp, FaTelegram, FaLinkedin } from 'react-icons/fa';
import {
  Save,
  ArrowBack,
  Store as StoreIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_STORE_BY_ID } from '../graphql/queries';
import { UPDATE_STORE } from '../graphql/mutations';
import LoadingSpinner from '../components/common/LoadingSpinner';
import CustomSnackbar, { SnackbarMessage } from '../components/common/CustomSnackbar';
import ImageUpload from '../components/common/ImageUpload';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const StoreEdit = () => {
  const { storeId: paramStoreId } = useParams();
  const { storeId: authStoreId } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  // Use store ID from params if available, otherwise use from auth
  const currentStoreId = paramStoreId || authStoreId;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    email: '',
    weekdayStartTime: '',
    weekdayEndTime: '',
    weekendStartTime: '',
    weekendEndTime: '',
    isActive: true,
    instagramUrl: '',
    facebookUrl: '',
    whatsappUrl: '',
    telegramUrl: '',
    linkedinUrl: ''
  });

  const [existingProfileImage, setExistingProfileImage] = useState<string>('');
  const [existingCarouselImages, setExistingCarouselImages] = useState<string[]>([]);
  const [removedProfileImage, setRemovedProfileImage] = useState<boolean>(false);
  const [removedCarouselImages, setRemovedCarouselImages] = useState<string[]>([]);

  const [errors, setErrors] = useState<string[]>([]);
  const [snackbar, setSnackbar] = useState<SnackbarMessage>({
    open: false,
    message: '',
    severity: 'success'
  });

  const { data: storeData, loading: storeLoading, error: storeError } = useQuery(GET_STORE_BY_ID, {
    variables: { storeId: currentStoreId },
    skip: !currentStoreId,
  });

  const [uploadProfileImageMutation] = useMutation(UPDATE_STORE);
  const [uploadCarouselImageMutation] = useMutation(UPDATE_STORE);

  const handleAutoUploadProfileImage = async (file: File): Promise<boolean> => {
    try {
      const base64Data = await fileToBase64(file);
      const result = await uploadProfileImageMutation({
        variables: { storeId: currentStoreId, fileName: file.name, fileData: base64Data },
      });
      if (result.data?.updateStore?.ok) {
        const avatar = result.data.updateStore.store?.avatar;
        if (avatar) setExistingProfileImage(avatar);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleAutoUploadCarouselImage = async (file: File): Promise<boolean> => {
    try {
      const base64Data = await fileToBase64(file);
      const result = await uploadCarouselImageMutation({
        variables: {
          storeId: currentStoreId,
          carouselImages: [{ fileName: file.name, fileData: base64Data }],
        },
      });
      if (result.data?.updateStore?.ok) {
        const images = result.data.updateStore.store?.images || [];
        const urls = images
          .filter((img: any) => img.imageType === 'carousel')
          .map((img: any) => img.image);
        setExistingCarouselImages(urls);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const [updateStore, { loading: updateLoading }] = useMutation(UPDATE_STORE, {
    onCompleted: (data) => {
      if (data.updateStore.ok) {
        setSnackbar({
          open: true,
          message: 'Store details updated successfully!',
          severity: 'success',
          title: 'Success'
        });
        setErrors([]);
        // Redirect immediately after successful save
        navigate(`/store/${currentStoreId}/details?updated=true`);
      } else {
        setErrors(data.updateStore.errors || ['An error occurred']);
        setSnackbar({
          open: true,
          message: 'Failed to update store details',
          severity: 'error',
          title: 'Error'
        });
      }
    },
    onError: (error) => {
      setErrors([error.message]);
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
        title: 'Error'
      });
    }
  });

  // Update form data when store data is loaded
  useEffect(() => {
    if (storeData?.storeDetails) {
      const store = storeData.storeDetails;

      setFormData({
        name: store.name || '',
        description: store.description || '',
        address: store.address || '',
        phone: store.phone || '',
        email: store.email || '',
        weekdayStartTime: store.weekdayStartTime || '',
        weekdayEndTime: store.weekdayEndTime || '',
        weekendStartTime: store.weekendStartTime || '',
        weekendEndTime: store.weekendEndTime || '',
        isActive: store.isActive ?? true,
        instagramUrl: store.instagramUrl || '',
        facebookUrl: store.facebookUrl || '',
        whatsappUrl: store.whatsappUrl || '',
        telegramUrl: store.telegramUrl || '',
        linkedinUrl: store.linkedinUrl || ''
      });

      if (store.avatar) {
        setExistingProfileImage(store.avatar);
      }

      if (store.images && store.images.length > 0) {
        const carouselImages = store.images
          .filter((img: any) => img.imageType === 'carousel')
          .map((img: any) => img.image);
        setExistingCarouselImages(carouselImages);
      }
    }
  }, [storeData]);

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix to get just the base64 string
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleRemoveExistingProfile = () => {
    setRemovedProfileImage(true);
    setExistingProfileImage('');
  };

  const handleRemoveExistingCarousel = (imageUrl: string) => {
    setRemovedCarouselImages(prev => [...prev, imageUrl]);
    setExistingCarouselImages(prev => prev.filter(url => url !== imageUrl));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setErrors([]);
    setSnackbar({ open: false, message: '', severity: 'success' });

    const variables: any = { storeId: currentStoreId, ...formData };
    if (removedProfileImage) variables.removeAvatar = true;
    if (removedCarouselImages.length > 0) variables.removeCarouselImages = removedCarouselImages;
    updateStore({ variables });
  };

  if (storeLoading) {
    return <LoadingSpinner message="Loading store details..." />;
  }

  if (storeError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading store: {storeError.message}
        </Alert>
      </Box>
    );
  }

  if (!storeData?.storeDetails) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Store not found
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      maxWidth: '1200px', 
      mx: 'auto',
      minHeight: '100vh',
      bgcolor: 'background.default'
    }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 },
          mb: 3,
          pb: 2,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom fontWeight={700} sx={{ mb: 1 }}>
              Edit Store
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Update your store information and settings
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            component={Link}
            to={`/store/${currentStoreId}/details`}
            sx={{ minWidth: 'fit-content' }}
          >
            Back to Details
          </Button>
        </Box>

        {/* Error Messages */}
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Form */}
        <Paper sx={{ 
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          boxShadow: theme.shadows[1],
          bgcolor: 'background.paper'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <StoreIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>
              Store Information
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Basic Information Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ 
                  mb: 2, 
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Store Name"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  required
                  variant="outlined"
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  variant="outlined"
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={handleInputChange('description')}
                  variant="outlined"
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={handleInputChange('address')}
                  variant="outlined"
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={handleInputChange('phone')}
                  variant="outlined"
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              {/* Operating Hours Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ 
                  mb: 2, 
                  mt: 2, 
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}>
                  Operating Hours
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Weekday Start Time"
                  type="time"
                  value={formData.weekdayStartTime}
                  onChange={handleInputChange('weekdayStartTime')}
                  variant="outlined"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Weekday End Time"
                  type="time"
                  value={formData.weekdayEndTime}
                  onChange={handleInputChange('weekdayEndTime')}
                  variant="outlined"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Weekend Start Time"
                  type="time"
                  value={formData.weekendStartTime}
                  onChange={handleInputChange('weekendStartTime')}
                  variant="outlined"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Weekend End Time"
                  type="time"
                  value={formData.weekendEndTime}
                  onChange={handleInputChange('weekendEndTime')}
                  variant="outlined"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              {/* Store Status Toggle */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body1" fontWeight={600}>
                        Store Active
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formData.isActive 
                          ? 'Store is active and available for services' 
                          : 'Store is inactive and excluded from available services'
                        }
                      </Typography>
                    </Box>
                  }
                  sx={{ mt: 2, mb: 1 }}
                />
              </Grid>

              {/* Social Media Links Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{
                  mb: 2,
                  mt: 2,
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}>
                  Social Media Links
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Instagram"
                  type="url"
                  placeholder="https://instagram.com/yourstore"
                  value={formData.instagramUrl}
                  onChange={handleInputChange('instagramUrl')}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaInstagram />
                      </InputAdornment>
                    )
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Facebook"
                  type="url"
                  placeholder="https://facebook.com/yourstore"
                  value={formData.facebookUrl}
                  onChange={handleInputChange('facebookUrl')}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaFacebook />
                      </InputAdornment>
                    )
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="WhatsApp"
                  type="url"
                  placeholder="https://wa.me/yourphone"
                  value={formData.whatsappUrl}
                  onChange={handleInputChange('whatsappUrl')}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaWhatsapp />
                      </InputAdornment>
                    )
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Telegram"
                  type="url"
                  placeholder="https://t.me/yourstore"
                  value={formData.telegramUrl}
                  onChange={handleInputChange('telegramUrl')}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaTelegram />
                      </InputAdornment>
                    )
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="LinkedIn"
                  type="url"
                  placeholder="https://linkedin.com/company/yourstore"
                  value={formData.linkedinUrl}
                  onChange={handleInputChange('linkedinUrl')}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaLinkedin />
                      </InputAdornment>
                    )
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              {/* Image Upload Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{
                  mb: 2,
                  mt: 2,
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}>
                  Store Images
                </Typography>
                <Box sx={{ 
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  bgcolor: 'background.default'
                }}>
                  <ImageUpload
                    onProfileImageChange={() => {}}
                    onCarouselImagesChange={() => {}}
                    profileImage={existingProfileImage}
                    carouselImages={existingCarouselImages}
                    onRemoveProfileImage={handleRemoveExistingProfile}
                    onRemoveCarouselImage={handleRemoveExistingCarousel}
                    onAutoUploadProfileImage={handleAutoUploadProfileImage}
                    onAutoUploadCarouselImage={handleAutoUploadCarouselImage}
                  />
                </Box>
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  gap: 2, 
                  mt: 3, 
                  pt: 3, 
                  borderTop: 1, 
                  borderColor: 'divider'
                }}>
                  <Button
                    variant="outlined"
                    component={Link}
                    to={`/store/${currentStoreId}/details`}
                    disabled={updateLoading}
                    sx={{ 
                      minWidth: 120,
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={updateLoading ? <CircularProgress size={20} /> : <Save />}
                    disabled={updateLoading}
                    sx={{ 
                      minWidth: 140,
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: theme.shadows[2],
                      '&:hover': {
                        boxShadow: theme.shadows[4]
                      }
                    }}
                  >
                    {updateLoading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>

        {/* Snackbar for success/error messages */}
        <CustomSnackbar
          snackbar={snackbar}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        />
      </motion.div>
    </Box>
  );
};

export default StoreEdit;
